import React, { useState } from "react";
import { View, TextInput, StyleSheet, Platform, Linking, Pressable } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Skeleton } from "@/components/ui/Skeleton";

interface EmailInputProps {
  label?: string;
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  required?: boolean;
  editable?: boolean;
  /** External error message (e.g., "Email already exists") */
  error?: string;
  /** Whether currently checking (shows loading state) */
  checking?: boolean;
  /** Show skeleton loading state */
  loading?: boolean;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function EmailInput({
  label,
  value,
  onChangeText,
  placeholder = "Enter email address",
  required = false,
  editable = true,
  error,
  checking = false,
  loading = false,
}: EmailInputProps) {
  const { colors } = useTheme();
  const [isFocused, setIsFocused] = useState(false);

  const isValidFormat = value.length === 0 || EMAIL_REGEX.test(value);
  const formatError = value.length > 0 && !isValidFormat;
  const hasError = formatError || !!error;
  const errorMessage = formatError ? "Invalid email address" : error;

  // Loading state - show skeleton
  if (loading) {
    return (
      <View style={styles.container}>
        {label && <ThemedText style={styles.label}>{label}</ThemedText>}
        <Skeleton width="100%" height={44} radius={8} />
      </View>
    );
  }

  // Readonly state - show as clickable link
  if (!editable) {
    return (
      <View style={styles.container}>
        {label && <ThemedText style={styles.label}>{label}</ThemedText>}
        <View
          style={[styles.inputContainer, { borderColor: colors.border, backgroundColor: colors.backgroundSecondary }]}
        >
          <MaterialCommunityIcons name="email-outline" size={18} color={colors.textSecondary} style={styles.icon} />
          {value ? (
            <Pressable onPress={() => Linking.openURL(`mailto:${value}`)} style={{ flex: 1 }}>
              <ThemedText style={[styles.linkText, { color: colors.primary }]}>{value}</ThemedText>
            </Pressable>
          ) : (
            <ThemedText style={[styles.input, { color: colors.placeholder }]}>—</ThemedText>
          )}
        </View>
      </View>
    );
  }

  const borderColor = hasError ? colors.ruby?.border || "red" : isFocused ? colors.primary : colors.border;

  return (
    <View style={styles.container}>
      {(label || hasError) && (
        <View style={styles.labelRow}>
          {label && (
            <ThemedText style={styles.label}>
              {label}
              {required && <ThemedText style={{ color: colors.ruby?.text || "red" }}> *</ThemedText>}
            </ThemedText>
          )}
          {hasError && (
            <ThemedText style={[styles.errorText, { color: colors.dangerText || colors.ruby?.text || "red" }]}>
              {errorMessage}
            </ThemedText>
          )}
        </View>
      )}
      <View style={[styles.inputContainer, { borderColor, backgroundColor: colors.backgroundSecondary }]}>
        <MaterialCommunityIcons
          name="email-outline"
          size={18}
          color={hasError ? colors.ruby?.text || "red" : isFocused ? colors.primary : colors.textSecondary}
          style={styles.icon}
        />
        <TextInput
          value={value}
          onChangeText={(text) => onChangeText(text.replace(/\s/g, "").trim())}
          onKeyPress={(e: any) => {
            if (e.nativeEvent.key === " ") {
              e.preventDefault?.();
            }
          }}
          placeholder={placeholder}
          placeholderTextColor={colors.placeholder}
          style={[styles.input, { color: colors.text }]}
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
          autoComplete="email"
          editable={editable}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            onChangeText(value.trim());
          }}
        />
        {checking ? (
          <MaterialCommunityIcons name="loading" size={18} color={colors.textSecondary} />
        ) : value.length > 0 && isValidFormat && !error ? (
          <MaterialCommunityIcons name="check-circle" size={18} color={colors.emerald?.text || "green"} />
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 4,
  },
  labelRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: "600",
  },
  inputContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    height: 44,
  },
  icon: {
    marginRight: 8,
  },
  input: {
    flex: 1,
    fontSize: 15,
    height: "100%",
    ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
  },
  linkText: {
    fontSize: 15,
    fontWeight: "500",
  },
  errorText: {
    fontSize: 12,
  },
});
